import { InfoCard, Link, Progress } from '@backstage/core-components';
import { Box, LinearProgress, Typography, makeStyles } from '@material-ui/core';
import { formatDuration } from './format';
import { outcomeOf } from './outcome';
import { OutcomePill } from './OutcomePill';
import type { Job, Pipeline } from './types';
import { slugQuery, useInsights, type Loaded } from './useInsights';

const useStyles = makeStyles(theme => ({
  // The card's header sets its subheader's parts side by side; these stack.
  subheader: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  kind: {
    fontSize: '0.875rem',
    color: 'var(--ui-text)',
  },
  meta: {
    fontFamily: 'var(--font-mono)',
    fontSize: '0.8rem',
    color: 'var(--ui-text-muted)',
    overflowWrap: 'anywhere',
  },
  stages: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: theme.spacing(2),
  },
  stage: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
    minWidth: 0,
  },
  stageName: { fontWeight: 600, overflowWrap: 'anywhere' },
  bar: { height: 4, borderRadius: 2 },
  barSuccess: { backgroundColor: '#2EA043' },
  barFailure: { backgroundColor: '#F85149' },
  barRunning: { backgroundColor: '#1F6FEB' },
  barNeutral: { backgroundColor: '#6E7681' },
}));

/**
 * What the card shows, in words (DX-225, specs/component-pipeline.md AC-14):
 * the newest deployment, or — for a repository that deploys nothing, or whose
 * deploy workflows hold no deployment — the newest run on its default branch.
 * "Success" under a heading that only says "Latest pipeline" read as a green
 * deployment for every repository in revenexx-apps, which deploy nothing.
 */
export function pipelineKindOf(pipeline: Pipeline): string {
  const run = pipeline.latestPipeline;
  if (run && pipeline.latestDeployment?.id === run.id) {
    return 'The newest deployment';
  }
  const branch = pipeline.defaultBranch ?? 'the default branch';
  return pipeline.deployWorkflows.length
    ? `No deployment among its deploy workflows’ runs — the newest run on ${branch}`
    : `No deploy workflow — the newest run on ${branch}`;
}

/** How full a stage's bar is: done is full, running is its share of steps. */
function progressOf(job: Job): number {
  const { kind } = outcomeOf(job);
  if (kind === 'running') {
    return job.stepsTotal > 0
      ? Math.round((job.stepsCompleted / job.stepsTotal) * 100)
      : 0;
  }
  if (job.conclusion === 'skipped') return 0;
  return 100;
}

const Stage = ({ job }: { job: Job }) => {
  const classes = useStyles();
  const outcome = outcomeOf(job);
  const barClass = {
    success: classes.barSuccess,
    failure: classes.barFailure,
    running: classes.barRunning,
    neutral: classes.barNeutral,
    unknown: classes.barNeutral,
  }[outcome.kind];
  let took: string;
  if (job.durationSec !== null) took = formatDuration(job.durationSec);
  else if (outcome.kind === 'running') took = outcome.label;
  else took = 'No duration';

  return (
    <div className={classes.stage} data-stage={job.name}>
      <Box display="flex" alignItems="center" gridGap={8}>
        <span className={classes.stageName}>{job.name}</span>
        <OutcomePill outcome={outcome} />
      </Box>
      <Box display="flex" gridGap={12}>
        <Typography variant="body2">{took}</Typography>
        {job.htmlUrl && (
          <Link to={job.htmlUrl} aria-label={`Logs of ${job.name}`}>
            Logs
          </Link>
        )}
      </Box>
      <LinearProgress
        variant="determinate"
        value={progressOf(job)}
        aria-label={`${job.name} progress`}
        className={classes.bar}
        classes={{ barColorPrimary: barClass }}
      />
    </div>
  );
};

const Stages = ({
  slug,
  runId,
  running,
  runState,
}: {
  slug: string;
  runId: number;
  /** While the run is going, its stages are asked for again every minute. */
  running: boolean;
  /** Changes when the run finishes, so the stages are asked once more then. */
  runState: string;
}) => {
  const classes = useStyles();
  const jobs = useInsights<{ jobs: Job[] }>(
    `/workflow-runs/${runId}/jobs?${slugQuery(slug)}`,
    { refreshWhile: () => running, refreshKey: runState },
  );
  if (jobs.loading) return <Progress />;
  if (jobs.error) {
    return (
      <Typography role="alert">
        Couldn't load the stages of this run from GitHub.
      </Typography>
    );
  }
  const list = jobs.data?.jobs ?? [];
  if (!list.length) {
    return <Typography>This run reports no jobs.</Typography>;
  }
  return (
    <div className={classes.stages}>
      {list.map(job => (
        <Stage key={job.id} job={job} />
      ))}
    </div>
  );
};

/**
 * The "Latest pipeline" card (specs/component-pipeline.md AC-1 to AC-5).
 *
 * Which run it shows is decided by the backend — the newest deployment, else
 * the newest run on the default branch — so the tab and anything else asking
 * cannot disagree. With no run at all there is no card: an empty frame titled
 * "Latest pipeline" would claim a pipeline exists.
 */
export const LatestPipelineCard = ({
  slug,
  pipeline,
}: {
  slug: string;
  pipeline: Loaded<Pipeline>;
}) => {
  const classes = useStyles();

  if (pipeline.loading) {
    return (
      <InfoCard title="Latest pipeline">
        <Progress />
      </InfoCard>
    );
  }
  if (pipeline.error) {
    return (
      <InfoCard title="Latest pipeline">
        <Typography role="alert">
          Couldn't load the latest pipeline from GitHub.
        </Typography>
      </InfoCard>
    );
  }
  const data = pipeline.data;
  const run = data?.latestPipeline;
  if (!data || !run) return null;

  const meta = [
    run.name,
    run.runNumber !== null ? `#${run.runNumber}` : undefined,
    run.headBranch ?? undefined,
    run.headSha ? run.headSha.slice(0, 7) : undefined,
    run.actor,
  ].filter(Boolean);

  return (
    <InfoCard
      title={
        <Box display="flex" alignItems="center" gridGap={8} flexWrap="wrap">
          <span>Latest pipeline</span>
          <OutcomePill outcome={outcomeOf(run)} />
        </Box>
      }
      subheader={
        <span className={classes.subheader}>
          <span className={classes.kind}>{pipelineKindOf(data)}</span>
          <span className={classes.meta}>{meta.join(' · ')}</span>
        </span>
      }
      action={
        <Box pt={1} pr={1}>
          <Link to={run.htmlUrl}>Open in GitHub Actions</Link>
        </Box>
      }
    >
      <Stages
        slug={slug}
        runId={run.id}
        running={outcomeOf(run).kind === 'running'}
        runState={`${run.status}/${run.conclusion}`}
      />
    </InfoCard>
  );
};
